import {
  Entry,
  EntryGroup,
  GoalToDimension,
  Campaign,
  Goal,
} from 'Generated/graphql-types'
import moment, { Moment } from 'moment/moment'
// TODO move these utils into new Goal folder when we remove showRevampedGoals FF sc-37420
import {
  aggregateDonations,
  extractUniqueDonationDates,
  filterValidDonations,
  sortDonationsByDate,
  CAMPAIGN_GOAL_START_LABEL,
} from 'Components/Charts/CampaignMultiLineChart/utils'
import {
  DimensionsData,
  DataPoint,
  ChartCalculationOptionsEnum,
  SeriesData,
} from './types'

export const MM_DD_YY = 'MM/DD/YY'
export const MM_DD_YYYY = 'MM/DD/YYYY'
export const GOAL_DATE_FORMAT = 'MM-DD-YYYY'
export const IMPACT_GOALS_PATH = '/impact/goals'
export const FUNDER_IMPACT_GOALS_PATH = '/funder/impact/goals'
export const FUNDER_IMPACT_GOAL_GROUP_PATH = '/funder/impact/goals/group'
export const FUNDER_DASHBOARD_PATH = '/funder/dashboard'

export const getChartSeriesDataFromEntryGroup = (
  entryGroups: EntryGroup[],
  goalToDimensions: GoalToDimension[]
) => {
  try {
    const dimensions: Record<number, DimensionsData> = {}

    goalToDimensions.forEach((goalToDimension) => {
      const { dimension } = goalToDimension
      const { id, title: label } = dimension

      if (!dimensions[id]) {
        dimensions[id] = {
          data: [],
          goalToDimensionId: goalToDimension.id,
          label,
        }
      }
    })

    entryGroups.forEach((group) => {
      const dateString = moment.utc(group.reportedAt).format(MM_DD_YY)

      group.Entry.forEach((entry) => {
        const { dimension } = entry.goalToDimension
        const { id } = dimension

        /*
          TODO: Filter our archived entries in `goals` resolver in API. Currently,
            Prisma `where` inside `includes` doesn't seem to work as documented
         */
        if (!group.archivedAt) {
          dimensions[id].data.push({ x: dateString, y: entry.value })
        }
      })
    })

    // TODO: Sort on backend? Currently, Prisma `orderBy` inside `includes` doesn't seem to work as documented
    return Object.entries(dimensions).map(([, value]) => ({
      ...value,
      data: value.data.sort((a, b) =>
        moment(a.x, MM_DD_YY).diff(moment(b.x, MM_DD_YY))
      ),
    }))
  } catch {
    return []
  }
}

export const sortByGoalDimensionTitle = (a: Entry, b: Entry) => {
  const aTitle = a.goalToDimension.dimension.title
  const bTitle = b.goalToDimension.dimension.title

  if (aTitle < bTitle) return -1
  if (aTitle > bTitle) return 1
  return 0
}

export const calculateAdditionalChartSeries = (
  series: SeriesData[] | DimensionsData[],
  shouldAverage: boolean
) => {
  const totalSeries: { data: number[]; label: string } = {
    data: [],
    label: shouldAverage ? 'Average' : 'Total',
  }

  series.forEach((category) => {
    category.data.forEach((dataPoint, index) => {
      if (!totalSeries.data[index]) totalSeries.data[index] = dataPoint.y
      else totalSeries.data[index] += dataPoint.y
    })
  })

  if (shouldAverage) {
    const averagedData = totalSeries.data.map(
      (dataPoint) => dataPoint / series.length
    )
    return { data: averagedData, label: totalSeries.label }
  }

  return totalSeries
}

export const getSeriesDataFromCampaignData = ({
  goal,
  goalCampaigns,
  chartCalculation,
}: {
  goal: Goal
  goalCampaigns?: Campaign[]
  chartCalculation: ChartCalculationOptionsEnum
}) => {
  let target = goal?.target || 0
  const seriesData: { [key: string]: DataPoint[] } = {}
  const isCumulative =
    chartCalculation === ChartCalculationOptionsEnum.cumulative

  if (goalCampaigns?.length) {
    // Extracts all unique createdAt dates for donations across all campaigns.
    let allDates = goalCampaigns
      ? extractUniqueDonationDates(goalCampaigns)
      : []

    // Set first point at zero with Start label
    allDates = [CAMPAIGN_GOAL_START_LABEL, ...allDates]

    // Aggregate the donation data for each campaign. This results in a list of accumulated donation amounts for each date and campaign
    const aggregateData = goalCampaigns.flatMap((campaign) => {
      let totalAmount = 0

      // Filter disputed or refunded donations
      const validDonations = filterValidDonations(campaign.donations || [])

      // Sort the donations for each campaign by date and then aggregate the amounts for each date.
      const sortedDonationsByDate = sortDonationsByDate(validDonations)
      const aggregatedDonations = aggregateDonations(sortedDonationsByDate)

      return allDates.map((date) => {
        totalAmount += aggregatedDonations[date] || 0
        return {
          date,
          campaignName: campaign.name || '',
          netAmount: isCumulative
            ? totalAmount
            : aggregatedDonations[date] || 0,
        }
      })
    })

    // For each unique date, accumulate the total donations raised by the campaign until that date.
    allDates.forEach((date) => {
      const dataForDate = aggregateData.filter(
        (dataItem) => dataItem.date === date
      )
      dataForDate.forEach((dataItem) => {
        if (!seriesData[dataItem.campaignName]) {
          seriesData[dataItem.campaignName] = []
        }
        seriesData[dataItem.campaignName].push({
          x:
            dataItem.date === CAMPAIGN_GOAL_START_LABEL
              ? dataItem.date
              : moment.utc(dataItem.date).format(MM_DD_YY),
          y: dataItem.netAmount / 100, // convert to dollars
        })
      })
    })

    // Set target value if goal target is empty
    if (!goal?.target) {
      const totalFundraisingTarget = goalCampaigns.reduce(
        (total, campaign) => total + (campaign.fundraisingTarget || 0),
        0
      )
      target = totalFundraisingTarget / 100 // convert to dollars
    }
  }

  const seriesDataFormatted: SeriesData[] = Object.keys(seriesData).map(
    (title: string) => ({
      title,
      data: seriesData[title],
    })
  )

  return {
    data: {
      chart: {
        calculationType: chartCalculation,
        id: goal.id,
        target,
        title: goal.title,
        unitOfMeasurement: goal.unitOfMeasurement,
        seriesData: seriesDataFormatted,
      },
    },
  }
}

export const switchDataVisualization = (
  seriesData: SeriesData[],
  type: ChartCalculationOptionsEnum
): SeriesData[] => {
  switch (type) {
    case ChartCalculationOptionsEnum.cumulative:
      return convertToCumulative(seriesData)
    case ChartCalculationOptionsEnum.incremental:
      return seriesData
    default:
      return seriesData
  }
}

const convertToCumulative = (seriesData: SeriesData[]): SeriesData[] =>
  seriesData.map(({ data, title }) => {
    let cumulativeValue = 0
    const cumulativeData = data.map((dataPoint) => {
      cumulativeValue += dataPoint.y
      return { ...dataPoint, y: cumulativeValue }
    })
    return { data: cumulativeData, title }
  })

export const filterByDateRange = (
  date: Date | string,
  filterStartDate: Moment | null,
  filterEndDate: Moment | null
) => {
  try {
    let dateToFilter = date
    if (typeof date === 'string') dateToFilter = new Date(date)

    if (filterStartDate && filterEndDate) {
      return (
        moment.utc(dateToFilter).isSameOrAfter(filterStartDate) &&
        moment.utc(dateToFilter).isSameOrBefore(filterEndDate)
      )
    }
    if (filterStartDate)
      return moment.utc(dateToFilter).isSameOrAfter(filterStartDate)
    if (!filterStartDate && filterEndDate)
      return moment.utc(dateToFilter).isSameOrBefore(filterEndDate)
    return true
  } catch {
    console.error('Error filtering by date range')
    return true
  }
}

export const formatNumberCompact = (value: number) => {
  const formatter = new Intl.NumberFormat('en', { notation: 'compact' })
  return formatter.format(value)
}
